/**
 * 達成率を計算するカスタムフック
 *
 * 週間ビュー（Weekly.tsx）と月間ビュー（Monthly.tsx）で
 * 重複していた達成率計算ロジックを共通化したもの。
 *
 * 達成率 = (予定日のうち完了した日数) / (予定日の総数) × 100
 *
 * Rails で言えば:
 * - app/services/habits/stats_service.rb の calculate_achievement_rate に相当
 *
 * @example
 * const days = getDaysInRange(startDate, endDate)
 * const { calculateRate } = useAchievementRate(days)
 *
 * const rate = calculateRate(habit, habit.completedDates)
 * console.log(`達成率: ${rate}%`)
 */

import { useCallback } from 'react'
import { useHabitSchedule } from './useHabitSchedule'
import { formatDateLocal } from '@/lib/date'
import type { Habit } from '@/types'

/**
 * useAchievementRate フックの戻り値
 */
interface UseAchievementRateReturn {
  /**
   * 達成率を計算
   *
   * @param habit - 計算対象の習慣
   * @param completedDates - 完了日の配列（"YYYY-MM-DD" 形式）
   * @returns 達成率（0〜100 の整数、小数点以下は四捨五入）
   */
  calculateRate: (habit: Habit, completedDates: string[]) => number
}

/**
 * 達成率計算フック
 *
 * @param days - 計算対象の日付範囲（Date オブジェクトの配列）
 * @returns calculateRate 関数を含むオブジェクト
 */
export function useAchievementRate(days: Date[]): UseAchievementRateReturn {
  const { isScheduled } = useHabitSchedule()

  /**
   * 達成率を計算
   *
   * 計算ロジック:
   * 1. 指定された日付範囲内で、習慣が予定されている日数をカウント
   * 2. その予定日のうち、実際に完了した日数をカウント
   * 3. (完了数 / 予定数) × 100 で達成率を算出
   *
   * 注意: 予定日が 0 日の場合は 0% を返す（ゼロ除算を防ぐ）
   */
  const calculateRate = useCallback(
    (habit: Habit, completedDates: string[]): number => {
      // 指定期間内で予定されている日数をカウント
      const scheduledCount = days.filter((day) =>
        isScheduled(habit, day)
      ).length

      // 予定日が 0 日なら達成率は 0%
      if (scheduledCount === 0) {
        return 0
      }

      // 完了した日数をカウント（予定日かつ完了日のもののみ）
      const completedCount = completedDates.filter((dateStr) =>
        days.some(
          (day) => formatDateLocal(day) === dateStr && isScheduled(habit, day)
        )
      ).length

      // 達成率を計算（小数点以下は四捨五入）
      return Math.round((completedCount / scheduledCount) * 100)
    },
    [days, isScheduled]
  )

  return { calculateRate }
}
